import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import scsLogo from "@/assets/scott-cunningham-logo.webp";
import { ReadingModeToggle } from "@/components/ReadingModeToggle";
import { useLinkSet } from "@/lib/site-content";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [showCta, setShowCta] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const linkItems = useLinkSet("header_nav");
  const links = linkItems.map((l) => ({ to: l.href, label: l.label }));
  const activeLink =
    links.find((l) => l.to !== "/" && pathname.startsWith(l.to)) ??
    (pathname === "/" ? links[0] : undefined);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => {
      setIsDesktop(mq.matches);
      if (mq.matches) setOpen(false);
    };
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 760px)");
    const update = () => setShowCta(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const collapsibleLinks = links;

  return (
    <header
      ref={headerRef}
      className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-border/60 bg-background/80"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
        <Link to="/about" className="group flex items-center gap-2.5 leading-none">
          <div className="relative my-[-12px] inline-block">
            <img
              src={scsLogo}
              alt="Scott Cunningham"
              className="h-[3.6rem] w-auto select-none saturate-[1.35] contrast-125 [filter:drop-shadow(0_1px_1px_rgba(0,0,0,0.35))] transition-transform duration-300 ease-out group-hover:scale-[1.08]"
              draggable={false}
              width={240}
              height={58}
              fetchPriority="high"
              decoding="async"
            />
            <span
              aria-hidden
              style={{
                WebkitMaskImage: `url(${scsLogo})`,
                maskImage: `url(${scsLogo})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
              className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(110deg,transparent_30%,rgba(255,255,255,0.7)_50%,transparent_70%)] opacity-0 transition-all duration-700 ease-out group-hover:translate-x-full group-hover:opacity-100"
            />
          </div>
        </Link>
        <nav className="flex items-center gap-1">
          {isDesktop && (
            <div key="desktop-links" className="flex items-center gap-1 animate-fade-in">
              {links.map((l) => {
                const isActive = activeLink?.to === l.to;
                if (isActive) {
                  return (
                    <Link
                      key={l.to}
                      to={l.to as any}
                      className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground transition-all duration-300 hover:border-primary hover:bg-primary/10 hover:shadow-[0_0_22px_-2px_var(--primary)]"
                    >
                      <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" />
                      <span className="leading-none">{l.label}</span>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={l.to}
                    to={l.to as any}
                    className="group/nav relative px-3 py-2 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-primary"
                  >
                    {l.label}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-x-3 bottom-1 h-px origin-center scale-x-0 bg-[linear-gradient(90deg,transparent,var(--primary),transparent)] opacity-0 transition-all duration-300 ease-out group-hover/nav:scale-x-100 group-hover/nav:opacity-100"
                    />
                  </Link>
                );
              })}
            </div>
          )}
          {showCta && (
            <Link
              to="/contact"
              className="ml-3 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-background transition-transform hover:-translate-y-0.5"
            >
              Let's Connect →
            </Link>
          )}
          {!isDesktop && <ReadingModeToggle variant="inline" />}
          {!isDesktop && (
            <button
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              aria-expanded={open}
              className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full transition-transform active:scale-90 animate-fade-in"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          )}
        </nav>
      </div>
      {!isDesktop && (
        <div
          className={`absolute right-0 top-full z-50 grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr] pointer-events-none"
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <nav className="flex flex-col items-end gap-2 px-6 py-4 lg:px-10">
              {[
                ...collapsibleLinks,
                ...(!showCta ? [{ to: "/contact", label: "Let's Connect →" } as const] : []),
              ].map((l, i, arr) => {
                const total = arr.length;
                const isCta = l.to === "/contact";
                const isActive = activeLink?.to === l.to;
                return (
                  <Link
                    key={l.to}
                    to={l.to as any}
                    onClick={() => setOpen(false)}
                    className={`inline-flex items-center justify-end rounded-full border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ease-out hover:-translate-y-0.5 ${
                      isCta
                        ? "border-foreground bg-foreground text-background hover:opacity-90"
                        : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
                    } ${
                      open
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 -translate-y-3 pointer-events-none"
                    } ${isActive && !isCta ? "text-foreground border-foreground" : ""}`}
                    style={{
                      transitionDelay: open ? `${i * 50}ms` : `${(total - 1 - i) * 30}ms`,
                    }}
                    activeOptions={{ exact: l.to === "/" }}
                  >
                    {isActive && !isCta && (
                      <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" />
                    )}
                    <span className="leading-none">{l.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
